import React, { useMemo } from 'react';
import { Grid, Typography } from '@mui/material';
import CanvasJSReact from '../../ui/canvasjs/canvasjs.react';
import PropTypes from 'prop-types';

const CanvasChart = CanvasJSReact.CanvasJSChart;

function FeaturedWellSurveyCharts({ data, units }) {
  const AXIS_FONT_COLOR = '#808080';
  const SURVEY_PLOT_COLOR = '#38b6ff';
  const tvdVsPoints = useMemo(() => {
    return data
      ?.filter(row => row?.vs !== undefined && row?.tvd !== undefined)
      ?.map(row => ({
        x: Number(row.vs),
        y: Number(row.tvd)
      })) || [];
  }, [data]);

  const ewNsPoints = useMemo(() => {
    return data
      ?.filter(row => row?.ew !== undefined && row?.ns !== undefined)
      ?.map(row => ({
        x: Number(row.ew),
        y: Number(row.ns)
      })) || [];
  }, [data]);

  const tvdVsDomain = useMemo(() => {
    if (!tvdVsPoints.length) return null;

    const xValues = tvdVsPoints.map(point => point.x);
    const yValues = tvdVsPoints.map(point => point.y);

    const minX = Math.min(...xValues);
    const maxX = Math.max(...xValues);
    const minY = Math.min(...yValues);
    const maxY = Math.max(...yValues);

    const xPadding = (maxX - minX) * 0.1;
    const yPadding = (maxY - minY) * 0.05;

    return {
      minX: minX - xPadding,
      maxX: maxX + xPadding,
      minY: minY - yPadding,
      maxY: maxY + yPadding,
    };
  }, [tvdVsPoints]);

  const ewNsDomain = useMemo(() => {
    if (!ewNsPoints.length) return null;

    const xValues = ewNsPoints.map(point => point.x);
    const yValues = ewNsPoints.map(point => point.y);

    const minX = Math.min(...xValues);
    const maxX = Math.max(...xValues);
    const minY = Math.min(...yValues);
    const maxY = Math.max(...yValues);

    const xPadding = (maxX - minX) * 0.1;
    const yPadding = (maxY - minY) * 0.1;

    return {
      minX: minX - xPadding,
      maxX: maxX + xPadding,
      minY: minY - yPadding,
      maxY: maxY + yPadding,
    };
  }, [ewNsPoints]);

  const tvdVsOptions = useMemo(() => ({
    animationEnabled: false,
    exportEnabled: false,
    zoomEnabled: false,

    axisX: {
      title: '',
      gridThickness: 1,
      labelFontColor: AXIS_FONT_COLOR,
      minimum: tvdVsDomain?.minX,
      maximum: tvdVsDomain?.maxX,
    },

    axisY: {
      title: '',
      reversed: true,
      gridThickness: 1,
      labelFontColor: AXIS_FONT_COLOR,
      minimum: tvdVsDomain?.minY,
      maximum: tvdVsDomain?.maxY,
    },

    data: [{
      type: 'line',
      markerSize: 0,
      dataPoints: tvdVsPoints,
      color: SURVEY_PLOT_COLOR,
      lineThickness: 4,
    }]
  }), [tvdVsPoints, units]);

  const planViewOptions = useMemo(() => ({
    animationEnabled: false,
    exportEnabled: false,
    zoomEnabled: false,

    axisX: {
      title: '',
      gridThickness: 1,
      labelFontColor: AXIS_FONT_COLOR,
      minimum: ewNsDomain?.minX,
      maximum: ewNsDomain?.maxX,
    },

    axisY: {
      title: '',
      gridThickness: 1,
      labelFontColor: AXIS_FONT_COLOR,
      minimum: ewNsDomain?.minY,
      maximum: ewNsDomain?.maxY,
    },

    data: [{
      type: 'line',
      markerSize: 0,
      dataPoints: ewNsPoints,
      color: SURVEY_PLOT_COLOR,
      lineThickness: 4,
    }]
  }), [ewNsPoints, units]);

  return (
    <Grid container spacing={4} sx={{ width: '100%' }}>
      <Grid item xs={12} md={8}>
        <CanvasChart
          options={{
            ...tvdVsOptions,
            height: 400,
          }}
        />

        <Typography
          align="center"
          sx={{
            mt: 2,
            fontWeight: 500,
            fontSize: '1.2rem'
          }}
        >
          True Vertical Section View
        </Typography>
      </Grid>

      <Grid item xs={12} md={4}>
        <CanvasChart
          options={{
            ...planViewOptions,
            height: 400,
          }}
        />

        <Typography
          align="center"
          sx={{
            mt: 2,
            fontWeight: 500,
            fontSize: '1.2rem'
          }}
        >
          Plan View
        </Typography>
      </Grid>
    </Grid>
  );
}

FeaturedWellSurveyCharts.propTypes = {
  data: PropTypes.any,
  units: PropTypes.object
};

export default React.memo(FeaturedWellSurveyCharts);
